import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useConsumerAuth } from '../context/ConsumerAuthContext.jsx';

function MyImpact() {
  const { user, updateProfile, loading } = useConsumerAuth();
  const [form, setForm] = useState(() => ({
    location: user?.location || '',
    zip: user?.zip || '',
    preferredBin: user?.preferredBin || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  if (!user && !loading) {
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    return (
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body text-sm text-neutral">Loading your profile…</div>
      </div>
    );
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    try {
      setSaving(true);
      await updateProfile(form);
      setMessage('Profile updated');
    } catch (err) {
      setError(err?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-primary">My impact</h1>
        <p className="max-w-xl text-sm text-neutral">
          Thanks for joining Pact, {user.name?.split(' ')[0] || 'friend'}! We&apos;ll show your verified drops and
          mail-back stats here once consumer dashboards go live. For now, keep your verification session handy—we email
          updates at each step.
        </p>
      </header>
      <section className="card bg-base-100 shadow-lg">
        <div className="card-body space-y-5 text-sm text-neutral">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Saved profile</p>
            <p className="text-xs text-neutral/80">
              We&apos;ll use this information to personalize your Pact experience.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="form-control">
                <label className="label" htmlFor="location">
                  <span className="label-text uppercase tracking-[0.14em] text-neutral">City / State</span>
                </label>
                <input
                  id="location"
                  name="location"
                  className="input input-bordered rounded-xl"
                  value={form.location}
                  onChange={handleChange}
                  placeholder="e.g. Brooklyn, NY"
                />
              </div>
              <div className="form-control">
                <label className="label" htmlFor="zip">
                  <span className="label-text uppercase tracking-[0.14em] text-neutral">ZIP code</span>
                </label>
                <input
                  id="zip"
                  name="zip"
                  className="input input-bordered rounded-xl"
                  value={form.zip}
                  onChange={handleChange}
                  placeholder="11211"
                />
              </div>
              <div className="form-control sm:col-span-2">
                <label className="label" htmlFor="preferredBin">
                  <span className="label-text uppercase tracking-[0.14em] text-neutral">Preferred bin location</span>
                </label>
                <input
                  id="preferredBin"
                  name="preferredBin"
                  className="input input-bordered rounded-xl"
                  value={form.preferredBin}
                  onChange={handleChange}
                  placeholder="Store or neighborhood"
                />
              </div>
            </div>
            {error ? (
              <div className="alert alert-error text-xs">
                <span>{error}</span>
              </div>
            ) : null}
            {message ? (
              <div className="alert alert-success text-xs">
                <span>{message}</span>
              </div>
            ) : null}
            <button type="submit" className="btn btn-primary rounded-full" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </form>
          <div className="flex flex-wrap gap-3">
            <Link to="/verify" className="btn btn-outline rounded-full">
              Verify another item
            </Link>
            <Link to="/bin-finder" className="btn btn-outline rounded-full">
              Find a bin
            </Link>
          </div>
          <div className="rounded-2xl border border-success/30 bg-success/10 p-3 text-xs text-neutral">
            Tip: Keep using the same email during verification so future stats can be linked to your profile
            automatically.
          </div>
        </div>
      </section>
    </div>
  );
}

export default MyImpact;
